// src/pages/Organizations.jsx

import React, { useState, useEffect } from "react";
import { FaPlus, FaSearch, FaTimes, FaEdit, FaRegGem } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import {
  CheckCircle,
  Building2,
  Hash,
  MapPin,
  Calendar,
  Package,
} from "lucide-react";
import SummaryApi from "../common";
import { CiDiscount1 } from "react-icons/ci";
const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (modalOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modalOpen]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const res = await fetch(SummaryApi.getOrganization.url, {
        method: SummaryApi.getOrganization.method || "GET",
        headers: { "Content-Type": "application/json" },
        // credentials: 'include',          // ← add if using cookies/auth
      });

      const result = await res.json();

      if (result.status === true && Array.isArray(result.data)) {
        setOrganizations(result.data);
        setFilteredOrganizations(result.data);
      } else {
        toast.error(result.message || "Failed to load organizations");
      }
    } catch (err) {
      console.error("Fetch organizations error:", err);
      toast.error("Cannot connect to server");
    } finally {
      setLoading(false);
    }
  };

  // Real-time search logic
  useEffect(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      setFilteredOrganizations(organizations);
      return;
    }
    const filtered = organizations.filter((org) =>
      [org.organization_name, org.organization_code, org.city].some((val) =>
        (val || "").toLowerCase().includes(term),
      ),
    );
    setFilteredOrganizations(filtered);
  }, [search, organizations]);
  const openOrgModal = (org = null) => {
    setSelectedOrg(org);
    setModalOpen(true);
  };

  const openSubscriptionModal = (org) => {
    setSelectedOrg(org);
    setSubModalOpen(true);
  };
  const openModal = (org = null) => {
    setEditOrg(org);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setEditOrg(null), 300);
  };

  const showSuccess = (title, msg) => {
    toast.custom(
      () => (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-md w-full bg-white shadow-2xl rounded-3xl border-b-4 border-green-600 overflow-hidden"
        >
          <div className="p-5 flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-green-100 flex items-center justify-center text-green-600">
              <CheckCircle size={28} />
            </div>
            <div>
              <p className="font-bold text-gray-900">{title}</p>
              <p className="text-sm text-gray-600 mt-1">{msg}</p>
            </div>
          </div>
        </motion.div>
      ),
      { duration: 3800, position: "top-center" },
    );
  };

  // ────────────────────────────────────────────────
  //                 Organization Card
  // ────────────────────────────────────────────────
  const OrganizationCard = ({ org }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -6, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className="group bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100/80 overflow-hidden flex flex-col h-full transition-all duration-300"
    >
      <div className="h-32 bg-gradient-to-br from-indigo-500 to-purple-600 relative p-4 flex items-end">
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={() => openSubscriptionModal(org)}
            className="p-2 bg-white/20 hover:bg-white/40 text-white rounded-lg backdrop-blur-md transition-colors"
            title="Manage Subscription"
          >
            <FaRegGem size={18} />
          </button>
          <button
            onClick={() => openOrgModal(org)}
            className="p-2 bg-white/20 hover:bg-white/40 text-white rounded-lg backdrop-blur-md transition-colors"
          >
            <FaEdit size={18} />
          </button>
        </div>
        <h3 className="text-white font-bold text-lg line-clamp-1">
          {org.organization_name}
        </h3>
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="font-bold text-xl text-gray-900 mb-3 line-clamp-2 group-hover:text-indigo-700 transition-colors">
          {org.organization_name || "Unnamed Organization"}
        </h3>

        <div className="space-y-3 text-sm text-gray-600 mb-6 flex-grow">
          <div className="flex items-center gap-2.5">
            <Hash size={16} className="text-gray-400 flex-shrink-0" />
            <span className="font-medium">Code:</span>
            <span className="text-gray-700 truncate">
              {org.organization_code || "—"}
            </span>
          </div>

          <div className="flex items-center gap-2.5">
            <Building2 size={16} className="text-gray-400 flex-shrink-0" />
            <span className="font-medium">Type:</span>
            <span className="text-gray-700 truncate">
              {org.organization_type || "—"}
            </span>
          </div>

          <div className="flex items-start gap-2.5">
            <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />

            <span className="font-medium">City:</span>
            <p className="text-gray-700">{org.city || "—"}</p>
          </div>

          {org.employee_strength && (
            <div className="text-gray-600 text-sm pl-6">
              ≈ {org.employee_strength.toLocaleString()} employees
            </div>
          )}
        </div>

        {/* <button
          onClick={() => openModal(org)}
          className="mt-auto w-full py-3.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-blue-700 transition-all shadow-md flex items-center justify-center gap-2 active:scale-95"
        >
          <FaEdit size={16} />
          Edit Organization
        </button> */}
      </div>
    </motion.div>
  );

  const SkeletonCard = () => (
    <div className="bg-white rounded-2xl shadow overflow-hidden animate-pulse flex flex-col h-full">
      <div className="h-44 bg-gradient-to-r from-gray-100 to-gray-200" />
      <div className="p-6 space-y-4 flex-grow">
        <div className="h-7 bg-gray-200 rounded w-5/6" />
        <div className="space-y-3">
          <div className="h-5 bg-gray-200 rounded w-4/6" />
          <div className="h-5 bg-gray-200 rounded w-3/6" />
          <div className="h-5 bg-gray-200 rounded w-5/6" />
        </div>
        <div className="h-12 bg-gray-200 rounded mt-6" />
      </div>
    </div>
  );

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />

      <div className="min-h-screen bg-gradient-to-b from-indigo-50/30 to-white pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-7xl">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-10 flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">
                Organizations
              </h1>
              <p className="text-slate-500">
                Manage partners and their subscription packages
              </p>
            </div>

            <div className="flex gap-4 w-full md:w-auto">
              <div className="relative flex-grow">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button
                onClick={() => openOrgModal(null)}
                className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-semibold flex items-center gap-2 hover:bg-indigo-700 transition"
              >
                <FaPlus /> Add Org
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-32"
            >
              <div className="text-9xl text-indigo-100 mb-8">🏢</div>
              <h2 className="text-3xl font-bold text-gray-700 mb-4">
                {search
                  ? "No matching organizations found"
                  : "No organizations yet"}
              </h2>
              <p className="text-lg text-gray-500 max-w-lg mx-auto">
                {search
                  ? "Try different search terms"
                  : 'Click "Add Organization" to create your first entry'}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {filteredOrganizations.map((org) => (
                <OrganizationCard
                  key={org.organization_id || org._id || org.organization_code}
                  org={org}
                />
              ))}
            </div>
          )}
        </div>

        {/* Modal */}
        <AnimatePresence>
          {modalOpen && (
            <OrganizationModal
              organization={selectedOrg}
              onClose={() => setModalOpen(false)}
              onSuccess={fetchOrganizations}
            />
          )}
          {subModalOpen && (
            <SubscriptionModal
              organization={selectedOrg}
              onClose={() => setSubModalOpen(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
const SubscriptionModal = ({ organization, onClose }) => {
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [subHistory, setSubHistory] = useState([]); // To store all previous records
  const [currentSub, setCurrentSub] = useState(null);

  const [loading, setLoading] = useState(false);
  const [fetchingPackages, setFetchingPackages] = useState(false);
  const [fetchingHistory, setFetchingHistory] = useState(true);
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    plan_id: "",
    package_id: "",
    start_date: today,
    end_date: "",
    is_active: 1,
  });
  const activePlanRunning =
    currentSub &&
    currentSub.status === "Active" &&
    new Date(currentSub.end_date) >= new Date();
  useEffect(() => {
    const orgId = organization.organization_id || organization._id;
    fetchHistory(orgId);
    fetchPlans();
  }, [organization]);

  // 1. Fetch Subscription History (includes Plan and Package names)
  const fetchHistory = async (orgId) => {
    setFetchingHistory(true);
    try {
      const res = await fetch(SummaryApi.getorgsubscriptionbyorgid.url, {
        method: SummaryApi.getorgsubscriptionbyorgid.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization_id: orgId }),
      });
      const result = await res.json();

      if (result.status && Array.isArray(result.data)) {
        // Sort by ID descending to get the latest first
        const sortedHistory = result.data.sort(
          (a, b) =>
            b.organization_subscription_id - a.organization_subscription_id,
        );
        setSubHistory(sortedHistory);

        // Set the most recent active/last record as the "Current"
        setCurrentSub(sortedHistory[0]);
      }
    } catch (err) {
      console.error("Error fetching history:", err);
      toast.error("Could not load subscription history");
    } finally {
      setFetchingHistory(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await fetch(SummaryApi.getActivePlan.url);
      const result = await res.json();
      if (result.status) setPlans(result.data);
    } catch (err) {
      toast.error("Failed to load plans");
    }
  };
  const handleStartDateChange = (newDate) => {
    // 2. Prevent setting a date before today via manual input
    if (newDate < today) {
      toast.error("Start date cannot be in the past");
      return;
    }

    const selectedPlan = plans.find(
      (p) => p.plan_id === parseInt(form.plan_id),
    );
    setForm((prev) => ({
      ...prev,
      start_date: newDate,
      end_date: calculateEndDate(newDate, selectedPlan?.duration),
    }));
  };
  const handlePlanChange = async (planId) => {
    const selectedPlan = plans.find((p) => p.plan_id === parseInt(planId));
    setForm((prev) => ({
      ...prev,
      plan_id: planId,
      package_id: "",
      end_date: calculateEndDate(prev.start_date, selectedPlan?.duration),
    }));

    if (!planId) {
      setPackages([]);
      return;
    }

    setFetchingPackages(true);
    try {
      const res = await fetch(SummaryApi.getPackageByPlanId.url, {
        method: SummaryApi.getPackageByPlanId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: planId }),
      });
      const result = await res.json();
      if (result.status) setPackages(result.data || []);
    } catch (err) {
      toast.error("Error fetching packages");
    } finally {
      setFetchingPackages(false);
    }
  };

  const calculateEndDate = (startDate, durationStr) => {
    if (!durationStr) return "";
    const months = parseInt(durationStr);
    if (isNaN(months)) return "";
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + months);
    return date.toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activePlanRunning) {
      return toast.error(
        `An active plan is already running until ${new Date(currentSub.end_date).toLocaleDateString("en-GB")}. New plans cannot be assigned yet.`,
      );
    }

    setLoading(true);
    const payload = {
      ...form,
      organization_id: organization.organization_id || organization._id,
      status: "Active",
    };

    try {
      const res = await fetch(SummaryApi.organization_subscription_create.url, {
        method: SummaryApi.organization_subscription_create.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.status) {
        toast.success("Subscription assigned successfully!");
        fetchHistory(organization.organization_id || organization._id); // Refresh history
        onClose();
      } else {
        toast.error(result.message || "Failed to assign");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-gradient-to-r from-indigo-600 to-blue-700 p-6 text-white flex justify-between items-center sticky top-0 z-20 shadow-md">
          <div>
            <h2 className="text-xl font-bold">Subscription Management</h2>
            <p className="text-indigo-100 text-sm">
              {organization.organization_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="p-8 space-y-10">
          {/* CURRENT ACTIVE PACKAGE BANNER */}
          <div className="bg-gradient-to-br from-white to-indigo-50/30 rounded-3xl p-6 border border-indigo-100 shadow-sm relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
            <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <CheckCircle size={14} /> Current Status
            </h3>

            {fetchingHistory ? (
              <div className="animate-pulse flex flex-col gap-4">
                <div className="h-10 bg-indigo-100 rounded-xl w-3/4" />
                <div className="h-6 bg-indigo-100/50 rounded-xl w-1/2" />
              </div>
            ) : currentSub ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                    Plan Name
                  </p>
                  <p className="text-xl font-extrabold text-slate-800 tracking-tight">
                    {currentSub.plan_name}
                  </p>
                  <p className="text-indigo-600 font-semibold text-sm mt-1">
                    {currentSub.package_name}
                  </p>
                </div>
                <div className="md:text-right">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                    Status
                  </p>
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${activePlanRunning ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                  >
                    {activePlanRunning
                      ? "ACTIVE & RUNNING"
                      : "EXPIRED / INACTIVE"}
                  </span>
                </div>

                <div className="col-span-1 border-t pt-4 border-indigo-50">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Valid From
                  </p>
                  <p className="text-sm font-semibold text-slate-700">
                    {new Date(currentSub.start_date).toLocaleDateString(
                      "en-GB",
                    )}
                  </p>
                </div>
                <div className="col-span-1 border-t pt-4 border-indigo-50 md:text-right">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Expires On
                  </p>
                  <p className="text-sm font-bold text-red-600">
                    {new Date(currentSub.end_date).toLocaleDateString("en-GB")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 italic">
                No previous subscription record found.
              </div>
            )}
          </div>

          {/* ASSIGN NEW FORM */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <FaPlus size={12} className="text-indigo-500" /> New Assignment
            </h3>
            <form
              onSubmit={handleSubmit}
              className="bg-slate-50/50 rounded-3xl p-6 border border-slate-100 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">
                    Subscription Plan
                  </label>
                  <select
                    required
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm"
                    value={form.plan_id}
                    onChange={(e) => handlePlanChange(e.target.value)}
                  >
                    <option value="">Choose Plan...</option>
                    {plans.map((p) => (
                      <option key={p.plan_id} value={p.plan_id}>
                        {p.plan_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">
                    Package Variant
                  </label>
                  <select
                    required
                    disabled={!form.plan_id || fetchingPackages}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50 shadow-sm"
                    value={form.package_id}
                    onChange={(e) =>
                      setForm({ ...form, package_id: e.target.value })
                    }
                  >
                    <option value="">
                      {fetchingPackages ? "Loading..." : "Choose Package..."}
                    </option>
                    {packages.map((pkg) => (
                      <option key={pkg.package_id} value={pkg.package_id}>
                        {pkg.package_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">
                    Activation Date
                  </label>
                  <input
                    type="date"
                    required
                    // 3. This physically prevents selecting past dates in the calendar UI
                    min={today}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                    value={form.start_date}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">
                    Auto Expiry
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-4 py-3 bg-indigo-50/50 border border-indigo-100 text-indigo-700 font-bold rounded-2xl outline-none"
                    value={form.end_date}
                    readOnly
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !form.package_id}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:shadow-xl hover:shadow-indigo-200 transform transition-all active:scale-95 disabled:grayscale"
              >
                {loading ? "Processing..." : "Update Subscription Plan"}
              </button>
            </form>
          </div>

          {/* SUBSCRIPTION HISTORY SECTION */}
          <div className="space-y-6 pb-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Hash size={12} className="text-indigo-500" /> Subscription
              History
            </h3>
            <div className="overflow-hidden border border-slate-100 rounded-3xl">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4">Plan / Package</th>
                    <th className="px-6 py-4">Duration</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {subHistory.length > 0 ? (
                    subHistory.map((sub) => (
                      <tr
                        key={sub.organization_subscription_id}
                        className="hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <p className="font-bold text-slate-700">
                            {sub.plan_name}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {sub.package_name}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-slate-600 font-medium">
                            {new Date(sub.start_date).toLocaleDateString(
                              "en-GB",
                            )}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            to{" "}
                            {new Date(sub.end_date).toLocaleDateString("en-GB")}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                              sub.status === "Active"
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {sub.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-6 py-8 text-center text-slate-400 italic"
                      >
                        No history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
// ────────────────────────────────────────────────
//             Create / Edit Organization Modal
// ────────────────────────────────────────────────
const OrganizationModal = ({ organization, onClose, onSuccess }) => {
  const isEdit = !!organization;

  const [form, setForm] = useState(
    isEdit
      ? {
          ...organization,
          is_active:
            organization.is_active === true || organization.is_active === 1,
        }
      : {
          organization_name: "",
          organization_type: "",
          employee_strength: "",

          city: "",
          mobile_number: "",
          contact_person: "",
          email: "",
          website: "",
          is_active: true,
        },
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.organization_name?.trim()) {
      return toast.error("Organization name is required");
    }

    const payload = {
      ...form,
      is_active: form.is_active ? 1 : 0,
      employee_strength: form.employee_strength
        ? Number(form.employee_strength)
        : null,
    };

    if (isEdit) {
      payload.organization_id =
        organization.organization_id || organization._id;
    }

    const api = isEdit
      ? SummaryApi.organizationUpdate
      : SummaryApi.organizationCreate;

    try {
      const res = await fetch(api.url, {
        method: api.method || "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        // credentials: 'include',   // ← if needed
      });

      const data = await res.json();

      if (data.status === true) {
        onSuccess();
        onClose();
      } else {
        toast.error(data.message || "Operation failed");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Network error — check connection");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/65 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.22 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[94vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b z-10 px-8 py-5 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? "Edit Organization" : "Create New Organization"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="text-2xl text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                name="organization_name"
                value={form.organization_name || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="Acme Corporation"
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Code <span className="text-red-500">*</span>
              </label>
              <input
                required
                name="organization_code"
                value={form.organization_code || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="ACM2025"
              />
            </div> */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <input
                name="organization_type"
                value={form.organization_type || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="IT Services, Manufacturing, Retail..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee Strength
              </label>
              <input
                type="number"
                name="employee_strength"
                value={form.employee_strength || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="250"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                name="city"
                value={form.city || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="Vijayawada"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                name="mobile_number"
                value={form.mobile_number || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="+91 98765 43210"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person
              </label>
              <input
                name="contact_person"
                value={form.contact_person || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="contact@acmecorp.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={form.website || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
              placeholder="https://www.acmecorp.com"
            />
          </div>

          <div className="pt-3">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="sr-only"
              />
              <div
                className={`w-14 h-7 rounded-full transition-colors duration-300 ${
                  form.is_active ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-300 ${
                    form.is_active ? "translate-x-7" : "translate-x-1"
                  } mt-0.5`}
                />
              </div>
              <span className="font-medium text-gray-700 text-lg">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-8 border-t mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-7 py-3 bg-gray-200 text-gray-800 font-semibold rounded-xl hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-bold rounded-xl hover:from-indigo-600 hover:to-blue-700 transition shadow-md"
            >
              {isEdit ? "Update Organization" : "Create Organization"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default Organizations;
